import { FindIcon } from '@/components/IconButton';
import { SceneEntity, SceneEntityTree } from '@/components/SceneEntityTree';
import { LAYER_FORMAT_TYPE } from '@/constants/layer';
import { Layer, useViewer } from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { Input, TreeNodeProps } from '@skand/ui';
import { debounce, difference } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LayerGroupNode } from './LayerGroupNode';
import { LayerNode } from './LayerNode';

interface LayerSelectorProps {
  selectedLayer: Layer | null;
  onSelect: (layer: Layer) => void;
  onClose: () => void;
}

const SKIPPED_ENTITIES = ['photoGroup', 'photo', 'annotationGroup', 'annotation'];
const SKIPPED_LAYER_FORMATS = difference(Object.values(LAYER_FORMAT_TYPE), [
  LAYER_FORMAT_TYPE.OBJ,
  LAYER_FORMAT_TYPE.IFC,
  LAYER_FORMAT_TYPE.MESH_3D,
]);

export const LayerSelector = ({ selectedLayer, onSelect, onClose }: LayerSelectorProps) => {
  const [searchKey, setSearchKey] = useState('');

  const visibleLayers = useViewer(state => state.visibleLayers);
  const layerGroups = useViewer(state => state.layerGroups);
  const layers = useViewer(state => state.layers);

  const visibleLayersSEIds = useMemo(() => {
    const sceneEntityIds = [];
    const layerElements = [...layerGroups, ...layers];

    for (const layer of layerElements) {
      if (visibleLayers.has(layer.id)) {
        sceneEntityIds.push(layer.sceneEntityId);
      }
    }

    return sceneEntityIds;
  }, [layerGroups, layers, visibleLayers]);

  // Debounced search handler
  const handleSearch = useMemo(() => {
    return debounce(setSearchKey, 300);
  }, [setSearchKey]);

  // Clean up the handler
  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  // Define Tree node component
  const TreeNode = useCallback(
    ({ data, isLeaf, depth, setOpen, isOpen }: TreeNodeProps<SceneEntity>) => {
      return (
        <div className={cn('w-full h-full')}>
          <div
            className={cn('flex', 'flex-row', 'items-center', 'flex-1', 'mr-2', 'w-full')}
            style={{ marginLeft: `${depth * 0.5}rem` }}
          >
            <div
              className={cn(
                !isLeaf ? 'i-skand-dropdown cursor-pointer' : 'h-1em',
                'min-w-1em',
                'text-neutral-400',
                'text-3',
                'mr-2',
                'ml-2',
                !isOpen && 'rotate--90',
                'transition-transform',
              )}
              onClick={() => setOpen(!isOpen)}
            />
            {data.entity.type === 'layerGroup' && <LayerGroupNode group={data.entity} />}
            {data.entity.type === 'layer' && (
              <LayerNode
                layer={data.entity}
                onClose={onClose}
                onSelect={onSelect}
                selected={selectedLayer?.id === data.entity.id}
              />
            )}
          </div>
        </div>
      );
    },
    [onClose, onSelect, selectedLayer],
  );

  return (
    <div
      className={cn(
        'z-10',
        'border-1',
        'border-neutral-400',
        'rounded-2',
        'border-solid',
        'bg-neutral-100',
      )}
      style={{
        width: 'var(--radix-dropdown-menu-trigger-width)',
        maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
      }}
    >
      <div className={cn('w-full', 'p-3')}>
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <p className={cn('typo-text-s', 'color-neutral-800')}>Layers</p>
          </div>
        </div>

        <div className={cn('w-full my-3')}>
          <Input
            data-testid="input"
            label="Search"
            onChange={handleSearch}
            tail={<FindIcon />}
            value={searchKey}
          />
        </div>

        <div className={cn('max-h-100 overflow-y-auto')}>
          <SceneEntityTree
            sceneEntityIds={visibleLayersSEIds}
            searchKey={searchKey}
            skipEmptyLayerGroups
            skippedEntities={SKIPPED_ENTITIES}
            skippedLayerFormats={SKIPPED_LAYER_FORMATS}
          >
            {TreeNode}
          </SceneEntityTree>
        </div>
      </div>
    </div>
  );
};
